import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from 'react-oidc-context'
import { useAuth as useDopaGEAuth } from '../../hooks/useAuth'
import apis from '../../libs/services'
import logo from '../../assets/images/logo.svg'
import LoadingSpinner from '../../components/atoms/LoadingSpinner/LoadingSpinner'
import { logger } from '../../utils'
import { getPostLoginDestination, getRedirectPath, buildUrlWithRedirect } from '../../utils/deeplink'

type AuthStatus = 'processing' | 'success' | 'error'

function SSOCallback (): ReactElement {
  const navigate = useNavigate()
  const auth = useAuth()
  const { setUserProfile } = useDopaGEAuth()
  const [status, setStatus] = useState<AuthStatus>('processing')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    const handleSSOCallback = async (): Promise<void> => {
      try {
        // Check if OIDC authentication is in progress
        if (auth.isLoading) {
          return // Wait for OIDC to complete
        }

        // Check for OIDC errors
        if (auth.error != null) {
          throw new Error(`SSO authentication failed: ${auth.error.message}`)
        }

        // Check if user is authenticated and has access token
        if (auth.user?.access_token == null) {
          throw new Error('No authentication token received from SSO provider.')
        }

        // Exchange Okta token for DopaGE token
        const response = await apis.validateSSOToken(auth.user.access_token)

        if (response?.data?.data?.accessToken != null) {
          // Store DopaGE access token
          localStorage.setItem('dopageAccessToken', response.data.data.accessToken)

          // Fetch user profile with DopaGE token
          const profileResponse = await apis.getUserProfile()
          if (profileResponse?.data?.data != null) {
            const profileData = profileResponse.data.data
            setUserProfile(profileData)

            setStatus('success')
            toast.success('SSO authentication successful!', {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 3000
            })

            // Navigate based on user role and terms acceptance
            if (profileData.user_profile.accepted_terms === true) {
              if (profileData?.role?.slug === 'admin') {
                navigate('/dashboard/all-assigned-students')
              } else {
                // ProtectedRoute's survey guard will redirect to survey if needed
                navigate(getPostLoginDestination())
              }
            } else {
              navigate('/terms-of-use')
            }
          } else {
            throw new Error('Failed to fetch user profile')
          }
        } else {
          throw new Error('Failed to validate SSO token')
        }
      } catch (error: any) {
        logger.error('SSO Callback Error:')
        logger.error(error)
        setStatus('error')
        setErrorMessage(error.message ?? 'An unexpected error occurred during authentication')

        toast.error('SSO authentication failed. Please try again.', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 5000
        })
      }
    }

    void handleSSOCallback()
  }, [navigate, auth.isLoading, auth.error, auth.user, setUserProfile])

  const handleRetry = (): void => {
    // Navigate back to welcome page with SSO enabled, preserving redirect
    navigate(buildUrlWithRedirect('/', getRedirectPath(), { sso: 'true' }))
  }

  const handleFallback = (): void => {
    // Navigate to regular email login, preserving redirect
    navigate(buildUrlWithRedirect('/', getRedirectPath()))
  }

  if (status === 'processing') {
    return (
      <div className='bg-black min-h-[100dvh] font-primary flex flex-col items-center justify-center'>
        <div className='absolute left-[40px] top-[40px]'>
          <img src={logo} className='w-[138px]' alt='DopaGE logo' />
        </div>

        <div className='flex flex-col items-center justify-center text-center'>
          <LoadingSpinner />
          <h1 className='font-bold text-[38px] text-dopage-lightGreen mt-8 mb-4'>
            Authenticating SSO
          </h1>
          <p className='text-[20px] text-dopage-lightGreen max-w-[440px]'>
            Your Portal is opening…
          </p>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className='bg-black min-h-[100dvh] font-primary flex flex-col items-center justify-center'>
        <div className='absolute left-[40px] top-[40px]'>
          <img src={logo} className='w-[138px]' alt='DopaGE logo' />
        </div>

        <div className='flex flex-col items-center justify-center text-center max-w-[600px] mx-auto px-8'>
          <h1 className='font-bold text-[38px] text-red-500 mb-6'>
            Authentication Error
          </h1>
          <p className='text-[20px] text-dopage-lightGreen mb-8'>
            {errorMessage}
          </p>
          <div className='flex gap-4 flex-wrap justify-center'>
            <button
              onClick={handleRetry}
              className='bg-dopage-primary text-black font-bold py-3 px-6 rounded text-[18px] hover:bg-opacity-90 transition-opacity'
            >
              Try SSO Again
            </button>
            <button
              onClick={handleFallback}
              className='bg-transparent border-2 border-dopage-lightGreen text-dopage-lightGreen font-bold py-3 px-6 rounded text-[18px] hover:bg-dopage-lightGreen hover:text-black transition-colors'
            >
              Use Email Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Success state - this should be brief as navigation happens immediately
  return (
    <div className='bg-black min-h-[100dvh] font-primary flex flex-col items-center justify-center'>
      <div className='absolute left-[40px] top-[40px]'>
        <img src={logo} className='w-[138px]' alt='DopaGE logo' />
      </div>

      <div className='flex flex-col items-center justify-center text-center'>
        <h1 className='font-bold text-[38px] text-dopage-primary mb-4'>
          Authentication Successful!
        </h1>
        <p className='text-[20px] text-dopage-lightGreen'>
          Redirecting you to your dashboard...
        </p>
      </div>
    </div>
  )
}

export default SSOCallback
